import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Sort } from '@angular/material/sort';

import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

import { AuthService } from 'app/providers/bridge-api/auth.service';
import { ActionPlan, Status } from 'app/providers/storage-api/actionplan.service';
import { ActionItemListFilter } from '../components/action-item-list-filter/action-item-list-filter.component';
import { ActionPlanDetailsTabOptions } from '../pages/action-plan-details/action-plan-details.component';

export interface IDataItemDefaults {
  id: string;
  isExpired: boolean;
  expiredTooltip: string;
  name: string;
  dueDate: Date;
  status: Status;
  assignee: string;
}

export interface IDataItem extends IDataItemDefaults {
  isExpanded: boolean;
  notes: string;
}

@Directive()
export abstract class ActionPlanDetailsTableTabBase<T extends IDataItem>
  implements OnInit, OnDestroy
{
  @Input() actionPlan: ActionPlan;
  @Input() options: ActionPlanDetailsTabOptions;

  @Output() optionsChange = new EventEmitter<ActionPlanDetailsTabOptions>();
  @Output() countChange = new EventEmitter<void>();

  isReadOnly: boolean;

  dataSource: T[] = [];
  dataList: T[] = [];
  isAllRowExpanded = false;
  sort: Sort = {
    active: 'due_date',
    direction: 'desc',
  };

  searchValue: string;
  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  isFilterModalOpen = false;
  filter: ActionItemListFilter;
  hasActiveFilter = false;

  constructor(authService: AuthService) {
    this.isReadOnly = authService.isReadOnly;

    this.search$
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((v) => this.handleSearch(v));
  }

  abstract displayedColumns: string[];

  protected abstract getDataList(): T[];

  protected abstract sorting: (a: T, b: T) => number;

  ngOnInit(): void {
    this.dataList = this.getDataList();

    this.setOptions();

    this.setDataSource();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected toggleAllRow(): void {
    this.dataSource.forEach((i) => (i.isExpanded = !this.isAllRowExpanded));

    this.isAllRowExpanded = !this.isAllRowExpanded;

    this.emitOptionsChange();
  }

  protected toggleRow(data: T): void {
    data.isExpanded = !data.isExpanded;

    this.setIsAllRowExpanded();

    this.emitOptionsChange();
  }

  protected sortChange(sort: Sort): void {
    this.sort = sort;

    this.setDataSource();

    this.emitOptionsChange();
  }

  protected search(value: string): void {
    this.search$.next(value.trim());
  }

  protected openFilterModal(): void {
    this.isFilterModalOpen = true;
  }

  protected closeFilterModal(filter: ActionItemListFilter | null): void {
    this.isFilterModalOpen = false;

    if (filter) {
      this.filter = filter;

      this.setHasActiveFilter();

      this.setDataSource();

      this.emitOptionsChange();
    }
  }

  protected setDataSource(): void {
    let dataList = [...this.dataList];

    dataList = this.applyFiltering(dataList);

    dataList = this.applySorting(dataList);

    this.dataSource = dataList;
  }

  protected getStatusValue(dataItem: IDataItem): number {
    switch (dataItem.status) {
      default:
      case 'NEW':
        return 0;
      case 'IN_PROGRESS':
        return 1;
      case 'COMPLETED':
        return 2;
    }
  }

  protected applySorting(dataList: T[]): T[] {
    return dataList.sort(this.sorting);
  }

  private applyFiltering(dataList: T[]): T[] {
    if (this.filter?.dueDate) {
      dataList = dataList.filter(
        (i) => this.filter.dueDate.start <= i.dueDate && i.dueDate <= this.filter.dueDate.end
      );
    }

    if (this.filter?.statuses) {
      dataList = dataList.filter((i) => this.filter.statuses.includes(i.status));
    }

    if (this.filter?.assignee) {
      dataList = dataList.filter((i) => i.assignee?.includes(this.filter.assignee));
    }

    if (this.searchValue) {
      dataList = dataList.filter((i) =>
        i.name.toLocaleLowerCase().includes(this.searchValue.toLocaleLowerCase())
      );
    }

    return dataList;
  }

  private handleSearch(value: string): void {
    this.searchValue = value;

    this.setDataSource();

    this.emitOptionsChange();
  }

  private setOptions(): void {
    if (this.options?.sort) {
      this.sort = this.options.sort;
    }

    if (this.options?.searchValue) {
      this.searchValue = this.options.searchValue;
    }

    if (this.options?.filter) {
      this.filter = this.options.filter;

      this.setHasActiveFilter();
    }

    if (this.options?.expandedRowIds) {
      this.options.expandedRowIds.forEach(
        (id) => (this.dataList.find((i) => i.id === id).isExpanded = true)
      );

      this.setIsAllRowExpanded();
    }
  }

  private setHasActiveFilter(): void {
    this.hasActiveFilter =
      this.filter.dueDate !== undefined ||
      this.filter.statuses !== undefined ||
      this.filter.assignee !== undefined;
  }

  private setIsAllRowExpanded(): void {
    this.isAllRowExpanded = this.dataList.every((i) => i.isExpanded);
  }

  private emitOptionsChange(): void {
    this.optionsChange.emit({
      sort: this.sort,
      searchValue: this.searchValue,
      filter: this.filter,
      expandedRowIds: this.dataList.filter((i) => i.isExpanded).map((i) => i.id),
    });
  }
}
